<template>
  <div class="project-information">
    <template v-if="isEngineer || canTenantRead">
      <div
        v-if="isTenant && !isWithdrawn"
        class="btn-group-for-tenant text-right"
      >
        <button
          v-if="!isEditing"
          class="btn btn--darkblue"
          @click="edit"
        >{{ $t('action.edit') }}</button>
        <button
          v-if="isEditing"
          class="btn btn--darkblue"
          @click="cancel"
        >{{ $t('action.cancel') }}</button>
        <button
          v-if="isEditing && status === draft"
          class="btn btn--orange"
          @click="save"
        >{{ $t('action.save') }}</button>
        <button
          v-if="isEditing"
          class="btn btn--orange"
          @click="openSubmitConfirm"
        >{{ status === draft ? $t('action.submit') : $t('action.save') }}</button>
      </div>
      <div class="project-overview">
        <ProjectBasicInformation
          ref="proj-basic-infor"
          class="project-overview__info"
          :class="baseInfoClass"
          :editable="isEditing && canEdit"
        />
        <ProjectQuickLinks
          v-if="!isEditing"
          class="project-overview__link"
          :openTnc="openTnc"
        />
      </div>
      <div class="tenant-users">
        <TenantRepresentative
          ref="tenant-representative"
          :editable="isEditing && canEdit"
        />
      </div>
    </template>
    <p v-else>
      {{ $t('noTabPermission') }}
    </p>
    <TermsAndConditions
      ref="tncProjectInfoViewPage"
      modalId="tncProjectInfoViewPage"
      :acceptCallback="isloadPageTncPopup ? loadPageTncCallback : termsConditionsCallback"
    />
    <ActionConfirmModal
      ref="submitConfirmModal"
      :modalId="submitModalId"
      :confirmTitle="$t('confirmSubmit')"
    />
    <Modal
      ref="diffTenancyInfo"
      :showCloseBtn="false"
      modalId="diffTenancyInfo"
    >
      <div slot="content">
        <p class="font-bold">
          {{ $t('tenantProjects.diffTenancyInfoHint') }}
        </p>
      </div>
      <div slot="btns">
        <button
          type="button"
          class="btn btn--orange"
          @click="closeDiffTenancyInfoModal()"
        >{{ $t('action.ok') }}</button>
      </div>
    </Modal>
    <InviteModal
      ref="inviteModal"
      :accountsList="toInviteAccountsList"
      :onInviteClick="inviteAccountsList"
      :listTitle="$t('tenancy.tenantRepresentative')"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { getCurrentInstance, onMounted, onUnmounted, ref, unref, computed } from 'vue-demi'
import ActionConfirmModal from '@/controls/ActionConfirmModal'
import Modal from '@/controls/Modal'
import InviteModal from '@/controls/Modal/InviteModal'
import TermsAndConditions from '@/pages/tenants/TermsAndConditions'
import { getBuildingFloorZoneMap } from '@/api/buildings/request'
import { getSubsidiaryTextArray } from '@/api/postman/util'
import { exportProjectPermit } from '@/api/printers/request'
import * as tenantProjectApi from '@/api/tenantProjects/request'
import { buildSubmitRequestBody, inviteUser } from '@/api/tenantProjects/util'
import { useRefModal } from '@/controls/Modal/modal.hook'
import { getContractorTypes, RepAccessTypes } from '@/modules/tenantProjects/contractors/consts'
import { WORK_PERMIT_STATUS, WORK_PERMIT_TYPES } from '@/modules/workPermits/consts'
import { utils as formUtils } from '@/plugin/InputChange'
import { useRouteParam, useRouteParamId } from '@/router/useRouter'
import { useStoreGetters } from '@/store'
import mutationTypes from '@/store/mutation-types'
import { aclHelper, tmsPermsTypes } from '@/utils/aclHelper'
import { ActionTypes } from '@/utils/consts'
import dateUtil from '@/utils/dateUtil'
import EventBus from '@/utils/eventBus'
import { displayI18nFullName } from '@/utils/fullName'
import { tenancyTypeMap, tenantProjectStatus } from '@/utils/locale'
import notification from '@/utils/notification'
import { getPhysicalSite } from '@/utils/siteUtils'
import TenantRepresentative from './TenantRepresentative'
import ProjectQuickLinks from './ProjectQuickLinks'
import ProjectBasicInformation from './ProjectBasicInformation'

export default {
  name: 'ProjectInformation',
  components: {
    ActionConfirmModal,
    TenantRepresentative,
    TermsAndConditions,
    ProjectBasicInformation,
    ProjectQuickLinks,
    Modal,
    InviteModal,
  },
  props: {
    canEdit: Boolean,
    setHeaderBtn: {
      type: Function,
      default: () => {},
      required: true,
    },
  },
  setup () {
    const { proxy } = getCurrentInstance()
    const projectId = useRouteParamId()

    const { canTenantRead, currentTenantProject, isWithdrawn, isNormalAccount, accountsReturnEmptyListIfEmpty } = useStoreGetters('tenantProject', ['canTenantRead', 'currentTenantProject', 'isWithdrawn', 'isNormalAccount', 'accountsReturnEmptyListIfEmpty'])
    const { currentMainContractor, currentContractors, isMainUser } = useStoreGetters('projectContractors', ['currentMainContractor', 'currentContractors', 'isMainUser'])
    const { canAccessAllProjects } = useStoreGetters('ccd', ['canAccessAllProjects'])
    const openTncEvent = EventBus.on('wp_open_tnc', () => {
      proxy.openTnc()
    })
    let toInviteAccountsList = ref([])
    const canAccountInvited = (item) => {
      const isContractorAdmin = lodash.get(item, 'TenantProjectAccount.isContractorAdmin')
      const hasInvited = lodash.get(item, 'TenantProjectAccount.invitedAt')
      if (isContractorAdmin || hasInvited) {
        return false
      }
      const isNormal = lodash.get(item, 'TenantProjectAccount.accessType') === RepAccessTypes.NORMAL
      const isContractorInvited = lodash.get(item, 'TenantProjectAccount.accessType') === RepAccessTypes.CONTRACTOR
      if (unref(canAccessAllProjects) || (unref(isMainUser) && isContractorInvited) || (unref(isNormalAccount) && isNormal)) {
        return true
      }
    }
    const showInvitePopup = () => {
      const toInvitedList = unref(accountsReturnEmptyListIfEmpty).filter(canAccountInvited)
      toInviteAccountsList.value = toInvitedList
      if (unref(toInviteAccountsList).length) {
        openInviteModal()
      }
    }
    const inviteAccountsList = async () => {
      await Promise.all(
        unref(toInviteAccountsList).map(async (account) => {
          await inviteUser(unref(projectId), account)
        }),
      )
      await proxy.loadTenantProject(unref(projectId))
    }
    const { modal: inviteModal, open: openInviteModal } = useRefModal()
    const invitePopupChecking = useRouteParam('invitePopupChecking')
    const isEditing = ref(false)
    onMounted(() => {
      proxy.setHeaderBtn(proxy.headerBtns, 0)
      if (unref(invitePopupChecking)) {
        showInvitePopup()
      }
    })
    onUnmounted(() => {
      openTncEvent.cancel()
    })
    return {
      isEditing,
      baseInfoClass: computed(() => (unref(isEditing) ? 'full-width' : '')),
      toInviteAccountsList,
      openInviteModal,
      inviteModal,
      showInvitePopup,
      inviteAccountsList,
      canTenantRead,
      currentTenantProject,
      isWithdrawn,
      isNormalAccount,
      accountsReturnEmptyListIfEmpty,
      currentMainContractor,
      currentContractors,
      projectId,
    }
  },
  data () {
    return {
      submitModalId: `tenantProjViewSubmit-${new Date().getTime()}-${Math.floor(Math.random() * 1000)}`,
      isloadPageTncPopup: true,
      draft: tenantProjectStatus.DRAFT,
    }
  },
  computed: {
    ...mapGetters('user', ['currentUser', 'isTenant', 'isEngineer']),
    ...mapGetters('workPermit', ['workPermitsInProject']),
    ...mapGetters('regions', ['regionName', 'enableWorkPermitsFeature']),

    projectPermit () {
      const projectPermits = this.workPermitsInProject.filter(item => item.self.type === WORK_PERMIT_TYPES.PROJECT)
      const sortedProjectPermits = lodash.orderBy(projectPermits, ['self.createdAt'], ['desc'])
      return lodash.get(sortedProjectPermits, '0.self', false)
    },
    isApprovalProjectPermit () {
      return this.projectPermit && this.projectPermit.status === WORK_PERMIT_STATUS.APPR
    },
    isAcceptedTnC () {
      return lodash.size(this.currentTenantProject.tncAcceptance) >= 2
    },
    canReadTenantProject () {
      return aclHelper.getAccess(tmsPermsTypes.project.read)
    },
    canGenerateNotice () {
      return aclHelper.getAccess(tmsPermsTypes.workpermit.generateNotice)
    },
    disableGenerateNoticeBtn () {
      return !(this.isAcceptedTnC && (this.canGenerateNotice ? true : this.isApprovalProjectPermit))
    },
    headerBtns () {
      const btns = []
      if (this.isTenant) { return btns }
      if (this.canReadTenantProject && this.isEngineer && this.enableWorkPermitsFeature) {
        btns.push({
          title: this.$t('settings.security.workPermit.generateNotice'),
          cls: 'btn--orange',
          func: this.generateNotice,
          disable: this.disableGenerateNoticeBtn,
        })
      }
      if (!this.canEdit) { return btns }

      btns.push(this.isEditing ? {
        title: this.$t('action.cancel'),
        cls: 'btn--darkblue',
        func: this.cancel,
      } : {
        title: this.$t('action.edit'),
        cls: 'btn--darkblue',
        func: this.edit,
      })
      if (this.isEditing && this.status === tenantProjectStatus.DRAFT) {
        btns.push({
          title: this.$t('action.save'),
          cls: 'btn--orange',
          func: this.save,
        })
      }
      if (this.isEditing) {
        btns.push({
          title: this.status === tenantProjectStatus.DRAFT ? this.$t('action.submit') : this.$t('action.save'),
          cls: 'btn--orange',
          func: this.openSubmitConfirm,
        })
      }
      return btns
    },
    tenancyInfoTitle () {
      const type = lodash.get(tenancyTypeMap, this.currentTenancy.type, '')
      const building = this.currentTenancy.building || '--'
      const tradeName = lodash.get(this.currentTenancy, 'tradeName', '')
      const companyName = lodash.get(this.currentTenancy, 'company.name', '')
      const name = tradeName || companyName || ''
      const title = `${this.descriptionEdit}: ${building}(${type})/${name}`
      return title
    },

    status () {
      return lodash.get(this.currentTenantProject, 'status', '').toLocaleUpperCase()
    },
    tncAcceptance () {
      return this.currentTenantProject.tncAcceptance || {}
    },
  },
  watch: {
    headerBtns () {
      this.setHeaderBtn(this.headerBtns, 0)
    },
  },
  methods: {
    ...mapMutations('tenantProject', {
      updateFieldByName: mutationTypes.TENANT_PROJECT_UPDATE_FIELD_NAME,
      cleanEmptyAccounts: mutationTypes.TENANT_PROJECT_CLEAN_EMPTY_ACCOUNTS,
    }),
    ...mapActions('tenantProject', ['updateTenantProjectAllInfo', 'loadTenantProject', 'updateTenantProjectStatus', 'isUpdateable']),
    changeActiveElement () {
      document.body.focus()
    },
    edit () {
      this.isEditing = true
      this.changeActiveElement()
    },
    async save () {
      if (this.status === tenantProjectStatus.DRAFT) {
        this.cleanEmptyAccounts()
        await this.updateTenantProjectAllInfo(true)
        formUtils.resetDirty()
      } else if (this.$refs['proj-basic-infor'].validate() && this.$refs['tenant-representative'].validate()) {
        const isUpdateable = await this.isUpdateable(this.$router.currentRoute.params.id)
        if (isUpdateable) {
          this.cleanEmptyAccounts()
          await this.updateTenantProjectAllInfo(true)
          await this.updateTenantProjectStatus({
            request: { status: tenantProjectStatus.DRAFT },
            action: ActionTypes.tenantProjects.resetToDraft,
          })
        }
      }
      this.isEditing = false
      this.changeActiveElement()
    },
    loadPageOpenTncModal () {
      this.isloadPageTncPopup = true
      this.openTnc()
    },
    async openSubmitConfirm () {
      const validateBasicInfo = this.$refs['proj-basic-infor'].validate()
      const validateAccounts = this.$refs['tenant-representative'].validate()
      if ((this.isTenant && this.status !== tenantProjectStatus.DRAFT) || await this.isDifferentTenancyInfo()) {
        if (validateBasicInfo && validateAccounts) {
          if (this.currentTenantProject.status === tenantProjectStatus.SUBMITTED) {
            this.beforeSubmit()
          } else {
            this.$refs.submitConfirmModal.open(this.beforeSubmit)
          }
        } else if (!this.isEditing) {
          notification.fireNotification(false, this.$t('tenantProjects.hintInputFields'))
        }
      } else {
        await this.openDiffTenancyInfoModal()
      }
    },
    closeDiffTenancyInfoModal () {
      this.$refs['diffTenancyInfo'].close()
    },
    async openDiffTenancyInfoModal () {
      await this.$refs['diffTenancyInfo'].open()
    },
    async isDifferentTenancyInfo () {
      const tenantProject = await tenantProjectApi.loadTenantProject(lodash.get(this.currentTenantProject, 'id'))
      return lodash.isEqual(lodash.get(tenantProject, 'tenancy.premisesDetailList'), lodash.get(this.currentTenantProject, 'tenancy.premisesDetailList'))
    },
    openTnc () {
      this.$refs.tncProjectInfoViewPage.openModal()
    },
    async beforeSubmit () {
      const validateBasicInfo = this.$refs['proj-basic-infor'].validate()
      const validateAccounts = this.$refs['tenant-representative'].validate()
      if (validateBasicInfo && validateAccounts) {
        const tenantUserAccepted = deep(this.currentTenantProject, 'tncAcceptance.tenantUser')
        const mainUserAccepted = this.isMainUser && deep(this.currentTenantProject, 'tncAcceptance.mainContractor')
        if (this.isEngineer || tenantUserAccepted || mainUserAccepted) {
          await this.submit()
          if (!this.isEngineer) {
            this.showInvitePopup()
          }
        } else {
          this.isloadPageTncPopup = false
          this.$refs.tncProjectInfoViewPage.openModal()
        }
      } else if (!this.isEditing) {
        notification.fireNotification(false, this.$t('tenantProjects.hintInputFields'))
      }
    },
    async termsConditionsCallback (isAccept) {
      if (isAccept) {
        await this.submit()
        this.showInvitePopup()
      }
    },
    async loadPageTncCallback (isAccept) {
      const isUpdateable = await this.isUpdateable(this.$router.currentRoute.params.id)
      if (isUpdateable) {
        await this.updateTenantProjectAllInfo()
      }
    },

    async submit () {
      const tenantProjectId = this.$router.currentRoute.params.id
      const beforeSubmitStatus = this.currentTenantProject.status
      const isUpdateable = await this.isUpdateable(tenantProjectId)
      if (isUpdateable) {
        if (beforeSubmitStatus === tenantProjectStatus.DRAFT) {
          const request = await buildSubmitRequestBody(this.currentTenantProject)
          if (request) {
            await tenantProjectApi.updateAndNotify(tenantProjectId, request, ActionTypes.tenantProjects.submit)
          }
        } else {
          const status = beforeSubmitStatus === tenantProjectStatus.IN_PROGRESS ? tenantProjectStatus.IN_PROGRESS : tenantProjectStatus.SUBMITTED
          await this.updateTenantProjectAllInfo()
          await this.updateTenantProjectStatus({
            request: { status },
            action: ActionTypes.tenantProjects.submit,
          })
        }
      }
      this.isEditing = false
      this.setHeaderBtn(this.headerBtns, 0)
      this.changeActiveElement()
      await this.loadTenantProject(this.$router.currentRoute.params.id)
    },
    async cancel () {
      if (this.$refs['proj-basic-infor'] && typeof this.$refs['proj-basic-infor'].resetValidation === 'function') {
        this.$refs['proj-basic-infor'].resetValidation()
      }
      await this.loadTenantProject(this.$router.currentRoute.params.id)
      this.isEditing = false
      formUtils.resetDirty()
      this.setHeaderBtn(this.headerBtns, 0)
      this.changeActiveElement()
    },
    addContractorDisplayName (originContractor) {
      const contractor = lodash.cloneDeep(originContractor)
      const contact = contractor.primaryContact
      contact.displayName = displayI18nFullName(contact.firstName, contact.lastName)
      return contractor
    },
    async generateNotice () {
      const tenancy = this.currentTenantProject.tenancy
      const tenancyCompany = tenancy.company
      const {BuildingFloorZoneMap} = await getBuildingFloorZoneMap()
      const building = BuildingFloorZoneMap[lodash.get(tenancy, 'building.code')]
      const site = getPhysicalSite(building.sites)
      const duration = this.projectPermit ? `${dateUtil.formatDate(this.projectPermit.startDate, true)} - ${dateUtil.formatDate(this.projectPermit.endDate, true)}` : ''
      const mainContractor = this.addContractorDisplayName(this.currentMainContractor)
      const contractors = this.currentContractors.map(contractor => {
        return this.addContractorDisplayName(contractor)
      })
      this.pdfId = await exportProjectPermit({
        date: dateUtil.format(new Date(), 'DD MMM YYYY'),
        mainContractor,
        contractors,
        premises: this.currentTenantProject.projectLocation,
        companyName: tenancyCompany.name,
        telephone: `${tenancyCompany.altPhone}  ${tenancyCompany.phone}`,
        duration,
        building: building.fullName || tenancy.building,
        code: this.projectPermit.code || '',
        contractorTypesList: getContractorTypes(),
        subsidiaryArray: getSubsidiaryTextArray(site.subsidiary),
        tnc: {
          title: this.$t(`terms.${this.regionName}.tenantProjects.title`),
          general: this.$t(`terms.${this.regionName}.tenantProjects.general`),
          generalCH: this.$t(`terms.${this.regionName}.tenantProjects.generalCH`),
          content: this.$t(`terms.${this.regionName}.tenantProjects.content`),
          remarks: this.$t(`terms.${this.regionName}.tenantProjects.remarks`),
          remarkGeneral: this.$t(`terms.${this.regionName}.tenantProjects.remarkGeneral`),
          remarkGenCH: this.$t(`terms.${this.regionName}.tenantProjects.remarkGenCH`),
          remarkContent: this.$t(`terms.${this.regionName}.tenantProjects.remarkContent`),
          agree: this.$t(`terms.${this.regionName}.tenantProjects.agree`),
          tenantUser: {
            companyName: this.tncAcceptance.tenantUser.companyName,
            agreeBy: this.$t(`terms.${this.regionName}.tenantProjects.agreedby`, [this.tncAcceptance.tenantUser.name, dateUtil.formatDate(this.tncAcceptance.tenantUser.acceptAt)]),
          },
          mainContractor: {
            agreeBy: this.$t(`terms.${this.regionName}.tenantProjects.agreedby`, [this.tncAcceptance.mainContractor.name, dateUtil.formatDate(this.tncAcceptance.mainContractor.acceptAt)]),
          },
        },
      })
    },
  },
}
</script>

<style lang="scss">
.project-information {
  background: #fafafa;
  .btn-group-for-tenant {
    background-color: #fff;
    padding: 0 0 15px;
  }
  .project-overview {
    display: flex;
    margin-bottom: 15px;
    &__info {
      width: 55%;
      box-shadow: 0 1px 5px rgba(128, 128, 128, 0.2);
      margin-right: 15px;
    }
    .full-width {
      width: 100%;
      margin-right: 0;
    }

    &__link {
      width: calc(45% - 15px);
      box-shadow: 0 1px 5px rgba(128, 128, 128, 0.2);
    }
  }
  .tenant-users {
    box-shadow: 0 1px 5px rgba(128, 128, 128, 0.2);
  }

}
</style>
